import React, { useEffect, useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";

const CODE_LENGTH = 6;
const RESEND_INTERVAL = 45;

const digitSx = {
    width: { xs: "42px", md: "52px" },
    "& .MuiOutlinedInput-root": {
        backgroundColor: "white",
        borderRadius: "10px",
        "&.Mui-focused fieldset": {
            borderColor: "#0b243d",
        },
    },
    "& input": {
        textAlign: "center",
        fontSize: { xs: "1.2rem", md: "1.4rem" },
        fontWeight: 600,
        color: "#0b243d",
        padding: { xs: "10px 0", md: "12px 0" },
    },
};

const StepTwo = ({ codigo, setCodigo, email, handleSendCode }) => {
    const [secondsLeft, setSecondsLeft] = useState(RESEND_INTERVAL);
    const [resending, setResending] = useState(false);
    const inputsRef = useRef([]);
    // Valor mais recente do código: o foco muda antes do re-render, então os handlers
    // não podem confiar na prop `codigo` (que ainda estaria com o valor anterior)
    const codigoRef = useRef(codigo);
    codigoRef.current = codigo;

    const updateCodigo = (value) => {
        codigoRef.current = value;
        setCodigo(value);
    };

    useEffect(() => {
        if (secondsLeft <= 0) return;
        const timer = setTimeout(() => setSecondsLeft((prev) => prev - 1), 1000);
        return () => clearTimeout(timer);
    }, [secondsLeft]);

    const handleResend = async () => {
        setResending(true);
        try {
            if (handleSendCode) await handleSendCode();
            updateCodigo("");
            setSecondsLeft(RESEND_INTERVAL);
            focusInput(0);
        } finally {
            setResending(false);
        }
    };

    const focusInput = (index) => {
        const input = inputsRef.current[index];
        if (input) {
            input.focus();
            input.select();
        }
    };

    // O código é uma string só de dígitos, preenchida em sequência (sem "buracos")
    const handleChange = (index, value) => {
        const atual = codigoRef.current;
        const digit = value.replace(/\D/g, "").slice(-1);
        if (!digit) {
            updateCodigo(atual.slice(0, index) + atual.slice(index + 1));
            return;
        }
        const position = Math.min(index, atual.length);
        updateCodigo((atual.slice(0, position) + digit + atual.slice(position + 1)).slice(0, CODE_LENGTH));
        if (position < CODE_LENGTH - 1) {
            focusInput(position + 1);
        }
    };

    const handleFocus = (index, e) => {
        // Não deixa pular caixas vazias: leva o foco para a próxima posição a preencher
        const atual = codigoRef.current;
        if (index > atual.length) {
            focusInput(atual.length);
            return;
        }
        e.target.select();
    };

    const handleKeyDown = (index, e) => {
        const atual = codigoRef.current;
        if (e.key === "Backspace" && !atual[index] && index > 0) {
            e.preventDefault();
            updateCodigo(atual.slice(0, index - 1));
            focusInput(index - 1);
        } else if (e.key === "ArrowLeft" && index > 0) {
            e.preventDefault();
            focusInput(index - 1);
        } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
            e.preventDefault();
            focusInput(index + 1);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
        if (!digits) return;
        updateCodigo(digits);
        focusInput(Math.min(digits.length, CODE_LENGTH - 1));
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Typography sx={{ mt: 0, mb: "20px", fontSize: { xs: "0.9rem", md: "0.95rem" }, color: "#555" }}>
                Enviamos um código de 6 dígitos para o seu e-mail: <strong>{email}</strong>. Digite-o abaixo para confirmar o seu cadastro.
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: { xs: "8px", md: "12px" },
                }}
            >
                {Array.from({ length: CODE_LENGTH }, (_, index) => (
                    <TextField
                        key={index}
                        value={codigo[index] || ""}
                        onChange={(e) => handleChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        onPaste={handlePaste}
                        onFocus={(e) => handleFocus(index, e)}
                        inputRef={(el) => (inputsRef.current[index] = el)}
                        autoFocus={index === 0}
                        variant="outlined"
                        sx={digitSx}
                        slotProps={{
                            htmlInput: {
                                inputMode: "numeric",
                                autoComplete: index === 0 ? "one-time-code" : "off",
                                "aria-label": `Dígito ${index + 1} do código`,
                            },
                        }}
                    />
                ))}
            </Box>

            <Typography variant="body2" color="textSecondary" sx={{ mt: "20px", textAlign: "center" }}>
                Não encontrou o e-mail? Verifique também a caixa de spam ou lixo eletrônico.
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mt: "10px" }}>
                <Button
                    onClick={handleResend}
                    disabled={secondsLeft > 0 || resending}
                    variant="text"
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#0b243d",
                        borderRadius: "10px",
                        "&:hover": { backgroundColor: "rgba(11, 36, 61, 0.06)" },
                    }}
                >
                    {resending
                        ? "Reenviando..."
                        : secondsLeft > 0
                            ? `Reenviar código em ${secondsLeft}s`
                            : "Reenviar código"}
                </Button>
            </Box>
        </Box>
    )
}

export default StepTwo;
